import {
    Box,
    Button,
    Card,
    ListItem,
    ListItemIcon,
    ListItemText,
    styled,
} from '@mui/material';
import StyPageContainer from 'ui/PageContainer/PageContainer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { green } from '@mui/material/colors';
import Flex from 'ui/Flex/Flex';

const STANDARD_PADDING = '4rem';

// Most of the values in these styles come from the Figma model.

const StyCtaContainer = styled(StyPageContainer, {
    name: 'StyCtaContainer',
    shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor: string }>(({ bgColor, theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    verticalAlign: 'middle',
    height: '100%',
    paddingLeft: STANDARD_PADDING,
    paddingRight: STANDARD_PADDING,
    backgroundColor: bgColor,
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        gap: '1rem',
    },
}));

const StyInfosWrapper = styled(Box, {
    name: 'StyInfosWrapper',
})(({ theme }) => ({
    height: 'max-content',
    flexBasis: '55%',
    minWidth: theme.typography.pxToRem(400),
    [theme.breakpoints.down('lg')]: {
        flexBasis: 'auto',
    },
}));

const StyCard = styled(Card, {
    name: 'StyCard',
    shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor: string }>(({ bgColor, theme }) => ({
    backgroundColor: bgColor,
    boxShadow: 'none',
    flexBasis: '40%',
    borderRadius: theme.typography.pxToRem(10),
    [theme.breakpoints.down('lg')]: {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'center',
        borderRadius: 0,
    },
}));

const StyMainButton = styled(Button, {
    name: 'StyMainButton',
})(() => ({
    background: 'primary.main',
    color: 'white',
}));

const StySecondaryButton = styled(Button, {
    name: 'StySecondaryButton',
})(() => ({
    color: 'primary.main',
}));

const StyListItemText = styled(ListItemText, {
    name: 'StyListItemText',
})(({ theme }) => ({
    '.MuiListItemText-primary': {
        fontSize: theme.typography.pxToRem(14),
        lineHeight: theme.typography.pxToRem(22.4),
        letterSpacing: theme.typography.pxToRem(0.43),
    },
}));

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})(({ theme }) => ({
    fontSize: theme.typography.pxToRem(43),
    marginBottom: '1.25rem',
    letterSpacing: theme.typography.pxToRem(0.43),
}));

const StySubTitle = styled(Typography, {
    name: 'StySubTitle',
})(({ theme }) => ({
    lineHeight: '1.8rem',
    letterSpacing: theme.typography.pxToRem(0.43),
}));

const StyListItemIcon = styled(ListItemIcon, {
    name: 'StyListItemIcon',
})(({ theme }) => ({
    alignSelf: 'flex-start',
    marginTop: theme.typography.pxToRem(4),
    marginBottom: theme.typography.pxToRem(4),
    minWidth: theme.typography.pxToRem(35),
}));

const FeatureList = ({ items: featureList }: { items: string[] }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ mt: '2rem', mb: '2rem' }}>
            {featureList.map((feature) => (
                <ListItem key={feature}>
                    <StyListItemIcon>
                        <CheckCircleIcon htmlColor={green[600]} />
                    </StyListItemIcon>
                    <StyListItemText primary={t(feature)} />
                </ListItem>
            ))}
        </Box>
    );
};

const CallToActionContent = ({
    title,
    description,
    featureList,
    callToActionHref,
    moreInfoHref,
    bgColor,
    children,
}: {
    title: string;
    description: string;
    featureList: string[];
    callToActionHref: URL;
    moreInfoHref: URL;
    bgColor: string;
    children: React.ReactNode;
}): React.ReactElement => {
    const { t } = useTranslation();

    return (
        <StyCtaContainer bgColor={bgColor}>
            <StyInfosWrapper>
                <StyTitle variant="h4">{t(title)}</StyTitle>
                <Box sx={{ mr: '2rem' }}>
                    <StySubTitle variant="h4">{t(description)}</StySubTitle>
                </Box>
                <FeatureList items={featureList} />
                <Flex sx={{ mt: '0.5rem' }}>
                    <Box sx={{ mr: '1.75rem', ml: '1rem' }}>
                        <a
                            href={callToActionHref.href}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <StyMainButton variant="contained">
                                {t('cta.get_a_quote')}
                            </StyMainButton>
                        </a>
                    </Box>
                    <Box>
                        <a
                            href={moreInfoHref.href}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <StySecondaryButton variant="outlined">
                                {t('cta.want_to_know_more')}
                            </StySecondaryButton>
                        </a>
                    </Box>
                </Flex>
            </StyInfosWrapper>
            <StyCard raised={false} bgColor={bgColor}>
                {children}
            </StyCard>
        </StyCtaContainer>
    );
};

export default CallToActionContent;
